import * as React from 'react';
import { st, classes } from './StatesButton.st.css';
import { Button } from '../Button';
import { deprecationLog, wrap, unwrap } from '../../common/deprecationLog';
import { Check } from '@wix/wix-ui-icons-common/on-stage';
import { StatesButtonProps, StatesButtonStates } from './types';
import { DataHooks } from './consts';

export class StatesButton extends React.Component<StatesButtonProps> {
  private timer: NodeJS.Timeout;

  constructor(props) {
    super(props);
    wrap('Button');
  }

  componentDidMount(): void {
    if (!this.props.upgrade) {
      deprecationLog(
        'StatesButton',
        'The current `StatesButton` component API will be deprecated in a future version. Please use the `upgrade` prop in order to use the new API.',
      );
      unwrap('Button');
    }
  }

  componentDidUpdate = ({ state: prevState }: StatesButtonProps) => {
    const { state: currentState, onNotificationEnd } = this.props;

    if (currentState !== prevState) {
      clearTimeout(this.timer);
    }

    if (
      (currentState === StatesButtonStates.SUCCESS &&
        prevState !== StatesButtonStates.SUCCESS) ||
      (currentState === StatesButtonStates.FAILURE &&
        prevState !== StatesButtonStates.FAILURE)
    ) {
      this.timer = setTimeout(
        () => onNotificationEnd && onNotificationEnd(),
        2000,
      );
    }
  };

  public buttonRef = React.createRef<HTMLButtonElement>();

  public focus = () => {
    this.buttonRef.current.focus();
  };

  private readonly debounceOnClick = (e) => {
    const { state, onClick } = this.props;
    if (state === StatesButtonStates.IDLE) {
      onClick(e);
    }
  };

  private renderCheck() {
    return (
      <div className={classes.successIconWrapper}>
        <Check
          className={classes.successIcon}
          data-hook={DataHooks.checkIcon}
        />
      </div>
    );
  }

  private renderContent(): React.ReactElement | string {
    const {
      state,
      idleContent,
      inProgressContent,
      failureContent,
      successContent,
    } = this.props;

    switch (state) {
      case StatesButtonStates.IDLE:
        return idleContent;
      case StatesButtonStates.IN_PROGRESS:
        return inProgressContent;
      case StatesButtonStates.FAILURE:
        return failureContent;
      case StatesButtonStates.SUCCESS:
        return successContent ? successContent : this.renderCheck();
      default:
        return idleContent;
    }
  }

  public render() {
    const {
      state,
      disabled,
      onClick,
      idleContent,
      inProgressContent,
      failureContent,
      successContent,
      onNotificationEnd,
      upgrade,
      className,
      ...rest
    } = this.props;
    const inProgress = state === StatesButtonStates.IN_PROGRESS;

    return (
      <Button
        className={st(classes.root, { upgrade }, className)}
        upgrade={upgrade}
        disabled={disabled}
        onClick={this.debounceOnClick}
        ref={this.buttonRef}
        aria-live="assertive"
        {...(inProgress && { 'aria-busy': true })}
        {...rest}
      >
        {this.renderContent()}
      </Button>
    );
  }
}
